import { useState } from "react";
import { useSelector } from "react-redux";
import { usePassengerCountString, useFlightDatesString } from "@apac/hooks";
import type { Airport } from "@b2bportal/air-shopping-api";
import {
  getAirports,
  getDestination,
  getOrigin,
} from "@b2bportal/core-flights";
import type { ITripTerminus } from "@b2bportal/core-types";
import { DateTimeFormatStyle } from "@hopper-b2b/i18n";

interface UseFlightShopTitleResponse {
  context: {
    tripDate: string;
    showSearch: boolean;
    originLabel: string;
    destinationLabel: string;
    passengerCounts: string;
  };
  handlers: {
    onEditClick: () => void;
    onCloseSearch: () => void;
  };
}

const getLocationLabel = (
  airportMap: Record<string, Airport> = {},
  location: ITripTerminus
): string => {
  if (!location || !location.id || !location.id.code) {
    return "";
  }

  const {
    code: { code: airportCode },
  } = location.id;
  const { cityName = "" } =
    airportMap?.[airportCode] ??
    Object.values(airportMap).find((a) => a.servedCity === airportCode) ??
    {};

  return cityName ? `${cityName} (${airportCode})` : airportCode;
};

export const useFlightShopTitle = (): UseFlightShopTitleResponse => {
  const tripDate = useFlightDatesString(
    DateTimeFormatStyle.ShortMonthDayShortWeekday
  );
  const passengerCounts = usePassengerCountString();
  const [showSearch, setShowSearch] = useState<boolean>(false);

  const origin = useSelector(getOrigin);
  const destination = useSelector(getDestination);
  const airportMap = useSelector(getAirports);

  const originLabel = getLocationLabel(airportMap, origin);
  const destinationLabel = getLocationLabel(airportMap, destination);

  const onEditClick = () => {
    setShowSearch(!showSearch);
  };

  return {
    context: {
      tripDate,
      showSearch,
      originLabel,
      destinationLabel,
      passengerCounts,
    },
    handlers: {
      onEditClick,
      onCloseSearch: () => {
        setShowSearch(false);
      },
    },
  };
};
